/*
 * Human-made content byline banner.
 * Chosen model: solid seal with a pencil mark.
 * Drop-in component for article/page bylines:
 *
 *   <HumanMadeBanner />                         // own block: hairline above and below
 *   <HumanMadeBanner bottomDivider={false} />   // above the footer: the footer edge closes the block
 *   <HumanMadeBanner disableTopSpacing />       // where the parent column already spaces its children
 *
 * The two switches are independent and all four combinations are in use — see the
 * call sites. Placement is expressed through them rather than through an inline
 * `style`, so spacing stays in styles.js.
 *
 * The seal and chip are also exported for standalone use (see seal.js / chip.js).
 */
import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { routes } from '../../router/routes'
import HumanMadeSeal from './seal'
import { getBadgeColors } from './colors'
import { useStyles } from './styles'
import useBadgeTranslation from './translation'

// The copy block's leading is tuned to this height — see styles.js.
const SEAL_SIZE = 88

const HumanMadeBanner = ({ bottomDivider = true, disableTopSpacing = false, dataTest = 'human-made-banner' }) => {
  const classes = useStyles()
  const theme = useTheme()
  const { sealBg } = getBadgeColors(theme.palette)
  const { t, ready } = useBadgeTranslation()

  // The badge is copy-only, so there is nothing worth showing until its bundle has landed.
  if (!ready) return null

  const href = routes['about-our-content'].path

  return (
    <Box
      className={clsx(classes.banner, {
        [classes.bannerNoBottomDivider]: !bottomDivider,
        [classes.bannerNoTopSpacing]: disableTopSpacing
      })}
      data-test={dataTest}
    >
      <Box className={classes.sealWrap}>
        {/* Decorative: the statement beside it already announces the same thing,
            so a label here would be read out twice. */}
        <HumanMadeSeal size={SEAL_SIZE} color={sealBg} decorative />
      </Box>
      <Box>
        <Typography component='p' className={classes.statement} data-test={`${dataTest}-statement`}>
          {t('human_made_statement')}
        </Typography>
        <Typography component='p' className={classes.details}>
          <a href={href} target='_blank' rel='noopener noreferrer' data-test={`${dataTest}-link`} className={classes.detailsLink} aria-label={`${t('human_made_link')} ${t('human_made_details')}`}>{t('human_made_link')}</a>
          {' '}
          {t('human_made_details')}
        </Typography>
      </Box>
    </Box>
  )
}

HumanMadeBanner.propTypes = {
  bottomDivider: PropTypes.bool,
  disableTopSpacing: PropTypes.bool,
  dataTest: PropTypes.string
}

export default HumanMadeBanner
export { default as HumanMadeSeal } from './seal'
export { default as HumanMadeChip } from './chip'
